import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { runFullAnalysis } from '@/lib/piiRulesEngine';
import { getIndustryRelevance, getStepHint, getMissedDataPoints, getIndustryLabel } from '@/lib/industryIntelligence';
import PIIFinderResults from '@/components/PIIFinderResults';
import {
  STEP_META, INDUSTRIES, BUSINESS_ACTIVITIES, DATA_SUBJECTS,
  DATA_ELEMENTS, SYSTEMS, DPDP_EXPLAINERS, STEP_COPY, WIZARD_LABELS,
} from '@/lib/content/wizardContent';
import {
  ArrowRight, ArrowLeft, Info, ShieldAlert, AlertTriangle, Lightbulb,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────
// SHARED UI
// ──────────────────────────────────────────────────────────

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

function DPDPExplainer({ text }) {
  return (
    <div className="mt-6 flex items-start gap-2.5 p-3 bg-[#003366]/5 border border-[#003366]/10 rounded-sm" data-testid="dpdp-explainer">
      <Info className="h-4 w-4 text-[#003366] shrink-0 mt-0.5" />
      <p className="text-xs text-[#003366]/70 leading-relaxed font-medium">
        <span className="font-bold text-[#003366]/90">Why this matters under DPDP: </span>{text}
      </p>
    </div>
  );
}

function IndustryInsightBar({ industry, stepKey }) {
  const hint = getStepHint(industry, stepKey);
  if (!hint) return null;
  const label = getIndustryLabel(industry);
  return (
    <div className="mb-4 flex items-start gap-2.5 p-3 bg-[#008080]/[0.04] border border-[#008080]/15 rounded-sm" data-testid={`industry-insight-${stepKey}`}>
      <Lightbulb className="h-3.5 w-3.5 text-[#008080] shrink-0 mt-0.5" />
      <div>
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#008080]">{label} Insight</span>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{hint}</p>
      </div>
    </div>
  );
}

function MissedDataCallout({ industry }) {
  const missed = getMissedDataPoints(industry).slice(0, 3);
  if (!missed.length) return null;
  const label = getIndustryLabel(industry);
  return (
    <div className="mt-4 bg-amber-50/80 border border-amber-200/60 rounded-sm p-3" data-testid="missed-data-callout">
      <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-amber-600">Commonly Missed in {label}</span>
      <div className="space-y-1.5 mt-2">
        {missed.map((item, i) => (
          <div key={i} className="flex items-start gap-2 text-[11px]" data-testid={`missed-item-${i}`}>
            <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold text-amber-800">{item.point}</span>
              <span className="text-slate-400 ml-1">&mdash; {item.note}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OptionCard({ selected, onClick, icon: Icon, label, sublabel, tag, tagColor, testId, isCommon }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`flex items-start gap-3 p-3.5 rounded-sm border-2 text-left transition-all duration-200 w-full ${
        selected ? 'border-[#008080] bg-[#008080]/[0.04] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
      }`}>
      <div className={`p-2 rounded-sm shrink-0 ${selected ? 'bg-[#008080] text-white' : 'bg-slate-100 text-slate-400'} transition-colors`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-semibold text-sm leading-tight ${selected ? 'text-[#003366]' : 'text-slate-700'}`}>{label}</span>
          {isCommon && (
            <span className="text-[7px] font-bold bg-[#008080]/10 text-[#008080] px-1.5 py-0.5 rounded leading-none">COMMON</span>
          )}
          {tag && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded leading-none ${
              tagColor === 'red' ? 'bg-red-100 text-red-700' : tagColor === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
            }`}>{tag}</span>
          )}
        </div>
        {sublabel && <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{sublabel}</p>}
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────
// STEPS 1–5 (selection steps — data-driven)
// ──────────────────────────────────────────────────────────

function StepIndustry({ value, onChange }) {
  return (
    <div>
      <h3 className="text-lg md:text-xl font-bold text-[#003366] mb-1">{STEP_COPY.industry.title}</h3>
      <p className="text-slate-400 text-sm mb-5">{STEP_COPY.industry.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {INDUSTRIES.map(ind => (
          <OptionCard key={ind.id} testId={`industry-${ind.id}`} selected={value === ind.id}
            onClick={() => onChange(ind.id)} icon={ind.icon} label={ind.label} />
        ))}
      </div>
      <DPDPExplainer text={DPDP_EXPLAINERS[0]} />
    </div>
  );
}

function StepActivities({ selected, onToggle, industry }) {
  const relevant = getIndustryRelevance(industry, 'activities');
  return (
    <div>
      <IndustryInsightBar industry={industry} stepKey="activities" />
      <h3 className="text-lg md:text-xl font-bold text-[#003366] mb-1">{STEP_COPY.activities.title}</h3>
      <p className="text-slate-400 text-sm mb-5">{STEP_COPY.activities.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {BUSINESS_ACTIVITIES.map(act => (
          <OptionCard key={act.id} testId={`activity-${act.id}`} selected={selected.includes(act.id)}
            onClick={() => onToggle(act.id)} icon={act.icon} label={act.label} sublabel={act.example}
            isCommon={relevant.includes(act.id)} />
        ))}
      </div>
      <DPDPExplainer text={DPDP_EXPLAINERS[1]} />
    </div>
  );
}

function StepSubjects({ selected, onToggle, industry }) {
  const relevant = getIndustryRelevance(industry, 'subjects');
  return (
    <div>
      <IndustryInsightBar industry={industry} stepKey="subjects" />
      <h3 className="text-lg md:text-xl font-bold text-[#003366] mb-1">{STEP_COPY.subjects.title}</h3>
      <p className="text-slate-400 text-sm mb-5">{STEP_COPY.subjects.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {DATA_SUBJECTS.map(sub => (
          <OptionCard key={sub.id} testId={`subject-${sub.id}`} selected={selected.includes(sub.id)}
            onClick={() => onToggle(sub.id)} icon={sub.icon} label={sub.label} sublabel={sub.desc}
            tag={sub.tag} tagColor={sub.tagColor}
            isCommon={relevant.includes(sub.id)} />
        ))}
      </div>
      <DPDPExplainer text={DPDP_EXPLAINERS[2]} />
    </div>
  );
}

function StepElements({ selected, onToggle, industry }) {
  const relevant = getIndustryRelevance(industry, 'elements');
  return (
    <div>
      <IndustryInsightBar industry={industry} stepKey="elements" />
      <h3 className="text-lg md:text-xl font-bold text-[#003366] mb-1">{STEP_COPY.elements.title}</h3>
      <p className="text-slate-400 text-sm mb-5">{STEP_COPY.elements.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {DATA_ELEMENTS.map(el => (
          <OptionCard key={el.id} testId={`element-${el.id}`} selected={selected.includes(el.id)}
            onClick={() => onToggle(el.id)} icon={el.icon} label={el.label} sublabel={el.example}
            tag={el.risk === 'critical' ? 'CRITICAL' : el.risk === 'high' ? 'HIGH RISK' : null}
            tagColor={el.risk === 'critical' ? 'red' : el.risk === 'high' ? 'amber' : null}
            isCommon={relevant.includes(el.id)} />
        ))}
      </div>
      <MissedDataCallout industry={industry} />
      <DPDPExplainer text={DPDP_EXPLAINERS[3]} />
    </div>
  );
}

function StepSystems({ selected, onToggle, industry }) {
  const relevant = getIndustryRelevance(industry, 'systems');
  return (
    <div>
      <IndustryInsightBar industry={industry} stepKey="systems" />
      <h3 className="text-lg md:text-xl font-bold text-[#003366] mb-1">{STEP_COPY.systems.title}</h3>
      <p className="text-slate-400 text-sm mb-5">{STEP_COPY.systems.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {SYSTEMS.map(sys => (
          <OptionCard key={sys.id} testId={`system-${sys.id}`} selected={selected.includes(sys.id)}
            onClick={() => onToggle(sys.id)} icon={sys.icon} label={sys.label} sublabel={sys.example}
            isCommon={relevant.includes(sys.id)} />
        ))}
      </div>
      <DPDPExplainer text={DPDP_EXPLAINERS[4]} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// MAIN MODULE
// ──────────────────────────────────────────────────────────

export default function PIIFinderModule() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [industry, setIndustry] = useState('');
  const [activities, setActivities] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [elements, setElements] = useState([]);
  const [systems, setSystems] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', company_name: '', role: '', company_size: '' });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  const toggle = (list, setter, id) => {
    setter(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const validate = () => {
    if (step === 0 && !industry) { toast.error('Select your industry to continue'); return false; }
    if (step === 1 && activities.length === 0) { toast.error('Select at least one business activity'); return false; }
    if (step === 2 && subjects.length === 0) { toast.error('Select at least one data subject category'); return false; }
    if (step === 3 && elements.length === 0) { toast.error('Select at least one data type'); return false; }
    if (step === 4 && systems.length === 0) { toast.error('Select at least one system or storage location'); return false; }
    return true;
  };

  const goNext = () => {
    if (!validate()) return;
    setDir(1);
    if (step === 4) {
      setAnalyzing(true);
      setStep(5);
      setTimeout(() => {
        const result = runFullAnalysis({ industry, activities, subjects, elements, systems });
        setAnalysis(result);
        setAnalyzing(false);
      }, 1800);
    } else {
      setStep(s => s + 1);
    }
  };

  const goBack = () => {
    if (step === 5) setAnalysis(null);
    setDir(-1);
    setStep(s => Math.max(0, s - 1));
  };

  const submitLead = async () => {
    if (!leadForm.name || !leadForm.email || !leadForm.company_name) { toast.error('Please fill in the required fields'); return; }
    setLeadLoading(true);
    try {
      await axios.post(`${API}/api/pii-check-lead`, {
        name: leadForm.name,
        email: leadForm.email,
        company_name: leadForm.company_name,
        role: leadForm.role,
        company_size: leadForm.company_size,
        industry,
        business_activities: activities,
        data_subjects: subjects,
        data_elements: elements,
        systems,
        impact_score: analysis?.summary?.score,
        impact_level: analysis?.summary?.impactLevel,
        is_sdf: analysis?.summary?.isSDF,
        pii_categories_count: analysis?.categories?.length,
        inferred_categories: analysis?.summary?.categories,
        impact_flags: analysis?.summary?.flags,
        blind_spots: analysis?.summary?.blindSpots,
        touchpoints: analysis?.touchpoints,
        visibility_gap_level: analysis?.visibilityGap?.level,
        visibility_gap_score: analysis?.visibilityGap?.score,
        next_steps: analysis?.nextSteps?.map(s => s.label),
      });
      setLeadSubmitted(true);
      toast.success('Your detailed DPDP impact summary is ready');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setLeadLoading(false); }
  };

  const isResults = step === 5;
  const showNav = !(isResults && (analysis || analyzing));

  return (
    <div className="bg-white border border-slate-200 shadow-2xl rounded-sm overflow-hidden" data-testid="pii-finder-module">
      {/* Progress header */}
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 sm:px-6 py-3.5">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#008080]">{WIZARD_LABELS.headerBadge}</span>
          <span className="text-[10px] font-bold text-slate-400">Step {Math.min(step + 1, 6)} of 6</span>
        </div>
        <div className="flex items-center gap-1">
          {STEP_META.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold transition-colors whitespace-nowrap ${
                  active ? 'bg-[#008080] text-white' : done ? 'bg-[#003366] text-white' : 'bg-slate-200/80 text-slate-400'
                }`}>
                  <Icon className="h-3 w-3" />
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < STEP_META.length - 1 && <div className={`flex-1 h-[2px] rounded ${done ? 'bg-[#003366]' : 'bg-slate-200'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {isResults && !analyzing && analysis ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <PIIFinderResults
            analysis={analysis}
            industry={industry}
            selections={{ industry, activities, subjects, elements, systems }}
            leadForm={leadForm}
            setLeadForm={setLeadForm}
            onSubmitLead={submitLead}
            leadSubmitted={leadSubmitted}
            leadLoading={leadLoading}
          />
        </motion.div>
      ) : (
        <div className="px-4 sm:px-8 py-6 sm:py-8 min-h-[400px]">
          <AnimatePresence mode="wait" custom={dir}>
            {analyzing ? (
              <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-16 text-center">
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: 'linear' }} className="mb-5">
                  <ShieldAlert className="h-14 w-14 text-[#008080]" />
                </motion.div>
                <h3 className="text-lg font-bold text-[#003366] mb-2">{WIZARD_LABELS.analyzingTitle}</h3>
                <p className="text-slate-400 text-xs max-w-sm">{WIZARD_LABELS.analyzingDescription}</p>
                <div className="w-48 mt-6 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <motion.div className="h-full bg-[#008080] rounded-full" initial={{ width: '10%' }}
                    animate={{ width: '90%' }} transition={{ duration: 1.6, ease: 'easeInOut' }} />
                </div>
              </motion.div>
            ) : (
              <motion.div key={step} custom={dir} variants={slideVariants}
                initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeOut' }}>
                {step === 0 && <StepIndustry value={industry} onChange={setIndustry} />}
                {step === 1 && <StepActivities selected={activities} onToggle={id => toggle(activities, setActivities, id)} industry={industry} />}
                {step === 2 && <StepSubjects selected={subjects} onToggle={id => toggle(subjects, setSubjects, id)} industry={industry} />}
                {step === 3 && <StepElements selected={elements} onToggle={id => toggle(elements, setElements, id)} industry={industry} />}
                {step === 4 && <StepSystems selected={systems} onToggle={id => toggle(systems, setSystems, id)} industry={industry} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Nav footer */}
      {showNav && (
        <div className="border-t border-slate-100 bg-slate-50/80 px-4 sm:px-6 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={goBack} disabled={step === 0} className="text-slate-400 text-sm" data-testid="pii-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> {WIZARD_LABELS.backButton}
          </Button>
          <Button onClick={goNext} className="bg-[#008080] hover:bg-[#006666] text-white rounded-sm px-6 font-bold text-sm" data-testid="pii-next-btn">
            {step === 4 ? WIZARD_LABELS.analyzeButton : WIZARD_LABELS.nextButton} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
